import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Observable, Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';

import { CardWrapperComponent } from '../../../shared/components/card-wrapper/card-wrapper.component';
import { CustomInputComponent } from '../../../shared/components/custom-input/custom-input.component';
import { UserFacade } from '../../../core/store/user/user.facade';
import { User, UserAddress } from '../../../core/models/user.model';
import { MindOrderCustomer } from '../../../core/models/mind-order.model';
import { formatPhone, formatZipCode } from '../../../core/utils/utils';
import { formatCnpj as formatCnpjUtil } from '../../../core/utils/cnpj.util';
import { digitsOnly } from '../../../core/utils/mind-order-payload.utils';
import { I18nPipe } from '../../../shared/pipes/i18n.pipe';

function cpfDigitsValidator(control: AbstractControl): ValidationErrors | null {
  const digits = digitsOnly(control.value);
  return digits.length === 11 ? null : { cpfInvalid: true };
}

@Component({
  selector: 'umkt-company-card',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardWrapperComponent,
    CustomInputComponent,
    I18nPipe,
  ],
  templateUrl: './company-card.component.html',
  styleUrl: './company-card.component.scss',
})
export class CompanyCardComponent implements OnInit, OnDestroy {
  @Input() canGoToNextStep = false;
  @Input() isCardLoading = false;
  @Input() isEditing = false;
  /** Quando true, coleta campos Figma que alimentam createOrder Mind. */
  @Input() enableMindCustomerFields = false;
  @Input() mindCustomer: Partial<MindOrderCustomer> | null = null;
  @Output() nextStep = new EventEmitter<void>();
  @Output() mindCustomerChange = new EventEmitter<Partial<MindOrderCustomer>>();

  userInformation$: Observable<User | null>;
  isLoading$: Observable<boolean>;
  error$: Observable<string | null>;
  isFormValid = false;

  missingUserFields: string[] = [];
  missingAddressFields: string[] = [];

  formatCnpj = formatCnpjUtil;
  formatPhone = formatPhone;
  formatZipCode = formatZipCode;

  private userFacade = inject(UserFacade);
  private fb = inject(FormBuilder);
  private destroy$ = new Subject<void>();
  private currentUser: User | null = null;

  mindForm = this.fb.group({
    contactName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    cpf: ['', [Validators.required, cpfDigitsValidator]],
    companyPhone: [''],
    marketingSms: [true],
    marketingCall: [true],
    marketingVoice: [true],
    marketingEmail: [true],
  });

  constructor() {
    this.userInformation$ = this.userFacade.getUserDetails();
    this.isLoading$ = this.userFacade.getIsLoading();
    this.error$ = this.userFacade.getError();
  }

  ngOnInit() {
    this.userInformation$.pipe(take(1)).subscribe(user => {
      if (!user) {
        this.userFacade.setLoading(true);

        this.userFacade
          .getUserUuid()
          .pipe(take(1))
          .subscribe(uuid => {
            if (uuid) {
              this.userFacade.loadUserDetails(uuid);
            } else {
              this.userFacade.setLoading(false);
            }
          });
      }
    });

    this.userInformation$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.currentUser = user;
      if (user) {
        this.patchMindFormFromUser(user);
        this.refreshValidity(user);
        this.checkMissingFields(user);
      }
    });

    this.mindForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.enableMindCustomerFields || !this.currentUser) {
        return;
      }
      this.refreshValidity(this.currentUser);
      if (this.mindForm.valid) {
        this.emitMindCustomer();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getPrimaryAddress(user: User | null): UserAddress | undefined {
    if (!user?.addresses?.length) return undefined;
    return user.addresses.find(a => a.main) ?? user.addresses[0];
  }

  get displayMindCustomer(): Partial<MindOrderCustomer> | null {
    return this.mindCustomer;
  }

  /** Telefone da empresa — só UI (não vai no createOrder). */
  get displayCompanyPhone(): string {
    const fromForm = digitsOnly(this.mindForm.get('companyPhone')?.value);
    if (fromForm) {
      return this.formatPhone(fromForm);
    }
    return this.currentUser?.phone
      ? this.formatPhone(this.currentUser.phone)
      : '';
  }

  onContinue(): void {
    if (this.enableMindCustomerFields) {
      this.mindForm.markAllAsTouched();
      if (!this.mindForm.valid) {
        return;
      }
      this.emitMindCustomer();
    }
    this.nextStep.emit();
  }

  /** Snapshot atual do customer Mind (para createOrder). */
  getMindCustomerSnapshot(): Partial<MindOrderCustomer> | null {
    if (!this.enableMindCustomerFields) {
      return this.mindCustomer;
    }
    const value = this.mindForm.getRawValue();
    return {
      companyName:
        this.currentUser?.business?.trim() ||
        this.mindCustomer?.companyName ||
        '',
      contactName:
        (value.contactName ?? '').trim() || this.mindCustomer?.contactName,
      email: (value.email ?? '').trim() || this.mindCustomer?.email,
      cpf: digitsOnly(value.cpf) || this.mindCustomer?.cpf,
      document:
        digitsOnly(this.currentUser?.document) || this.mindCustomer?.document,
      marketingDelivery: {
        sms: value.marketingSms ?? this.mindCustomer?.marketingDelivery?.sms ?? true,
        call: value.marketingCall ?? this.mindCustomer?.marketingDelivery?.call ?? true,
        voice: value.marketingVoice ?? this.mindCustomer?.marketingDelivery?.voice ?? true,
        email: value.marketingEmail ?? this.mindCustomer?.marketingDelivery?.email ?? true,
      },
    };
  }

  private patchMindFormFromUser(user: User): void {
    if (!this.enableMindCustomerFields) {
      return;
    }
    const contactName =
      this.mindCustomer?.contactName ||
      `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
      user.displayName ||
      '';
    this.mindForm.patchValue(
      {
        contactName,
        email: this.mindCustomer?.email || user.email || '',
        cpf: digitsOnly(this.mindCustomer?.cpf),
        companyPhone: digitsOnly(user.phone),
        marketingSms: this.mindCustomer?.marketingDelivery?.sms ?? true,
        marketingCall: this.mindCustomer?.marketingDelivery?.call ?? true,
        marketingVoice: this.mindCustomer?.marketingDelivery?.voice ?? true,
        marketingEmail: this.mindCustomer?.marketingDelivery?.email ?? true,
      },
      { emitEvent: false }
    );
  }

  private emitMindCustomer(): void {
    const snapshot = this.getMindCustomerSnapshot();
    if (snapshot) {
      this.mindCustomerChange.emit(snapshot);
    }
  }

  private refreshValidity(user: User): void {
    const baseValid = this.isUserDataComplete(user);
    if (!this.enableMindCustomerFields) {
      this.isFormValid = baseValid;
      return;
    }
    this.isFormValid = baseValid && this.mindForm.valid;
  }

  private isUserDataComplete(user: User): boolean {
    const primaryAddress = this.getPrimaryAddress(user);

    const hasUserData = !!(
      user.document?.trim() &&
      user.business?.trim() &&
      user.displayName?.trim() &&
      user.email?.trim() &&
      user.firstName?.trim() &&
      user.phone?.trim()
    );

    const hasAddressData = !!(
      primaryAddress?.address?.trim() &&
      primaryAddress?.number?.trim() &&
      primaryAddress?.neighborhood?.trim() &&
      primaryAddress?.city?.trim() &&
      primaryAddress?.state?.trim() &&
      primaryAddress?.zipCode?.trim()
    );

    return hasUserData && hasAddressData;
  }

  private checkMissingFields(user: User): void {
    const primaryAddress = this.getPrimaryAddress(user);

    this.missingUserFields = [];
    this.missingAddressFields = [];

    if (!user.document?.trim()) this.missingUserFields.push('CNPJ');
    if (!user.business?.trim()) this.missingUserFields.push('Razão Social');
    if (!user.displayName?.trim()) this.missingUserFields.push('Nome Fantasia');
    if (!user.email?.trim()) this.missingUserFields.push('E-mail');
    if (!user.firstName?.trim()) this.missingUserFields.push('Nome');
    if (!user.phone?.trim()) this.missingUserFields.push('Telefone');

    if (!primaryAddress?.address?.trim()) this.missingAddressFields.push('Endereço');
    if (!primaryAddress?.number?.trim()) this.missingAddressFields.push('Número');
    if (!primaryAddress?.neighborhood?.trim()) this.missingAddressFields.push('Bairro');
    if (!primaryAddress?.city?.trim()) this.missingAddressFields.push('Cidade');
    if (!primaryAddress?.state?.trim()) this.missingAddressFields.push('Estado');
    if (!primaryAddress?.zipCode?.trim()) this.missingAddressFields.push('CEP');
  }
}
